import { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '../components/ui/button'
import { pagePanel } from './layout/page-styles'
import { cn } from '../lib/utils'
import i18n from '../i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-page p-4">
          <div className={cn(pagePanel, 'w-full max-w-md p-8')}>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-error-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-neutral-900 mb-2">{i18n.t('errorPage.unexpectedTitle')}</h1>
              <p className="text-sm text-neutral-500 mb-6">
                {i18n.t('errorPage.unexpectedMessage')}
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-xs font-medium text-neutral-500 mb-2">
                    {i18n.t('common.devErrorDetails')}
                  </summary>
                  <div className="bg-neutral-50 border border-neutral-100 p-3 rounded-lg text-xs font-mono text-neutral-600 overflow-auto max-h-32">
                    <p className="mb-1"><strong>Error:</strong> {this.state.error.message}</p>
                    {this.state.errorInfo && (
                      <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                    )}
                  </div>
                </details>
              )}

              <div className="flex gap-3 justify-center">
                <Button onClick={this.handleReset} variant="secondary" size="md">
                  {i18n.t('errorPage.retry')}
                </Button>
                <Button onClick={this.handleReload} variant="primary" size="md">
                  {i18n.t('errorPage.reloadPage')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export function useErrorHandler() {
  return (error: Error, errorInfo?: ErrorInfo) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo)
  }
}
